import { getArticleLargeCardBody } from '@entities/articles/helpers.tsx';
import { useSaveData } from '@entities/common/savedData/index.ts';
import { memo, useCallback, useEffect, useLayoutEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useArticleLargeCarPagedRedirect } from '@pages/articles/articlePage/helpers.ts';
import { ArticleDropMenu } from '@features/articles/article-card/menu/index.ts';
import { PageBackButton } from '@features/common/PageBackButton/index.tsx';
import { ModerationStatusViewer } from '@features/common/SmallCardStatus/ModerationStatus/index.tsx';
import { getFullNameM } from '@shared/helpers/index.ts';
import { useAppDispatch, useUser } from '@shared/hooks/index.ts';
import { useModuleByCodeParam } from '@shared/hooks/use-module-by-code-param.ts';
import { ListBody, PageHead } from '@shared/ui/index.ts';
import { CommentsSmallButton } from '@shared/ui/likes_comments/CommentsSmallButton/index.tsx';
import { LikesSmallButton } from '@shared/ui/likes_comments/LikesSmallButton/index.tsx';
import { useLikeArticleMutation } from '@src/api/api-hooks/articles/useLikeArticleMutation.ts';
import { useMakeArticleFavoriteMutation } from '@src/api/api-hooks/articles/useMakeArticleFavoriteMutation.ts';
import { useRecommendedArticleQuery } from '@src/api/api-hooks/articles/useRecommendedArticleQuery.ts';
import { useRemoveArticleLikeMutation } from '@src/api/api-hooks/articles/useRemoveArticleLikeMutation.ts';
import { openDialogModal, openRecommendModal } from '@src/library/redux/modal/index.ts';

type PT = {};
export const RecommendedArticlePage = memo(({}: PT) => {
  const { hash } = useParams();
  const dispatch = useAppDispatch();
  const { code } = useModuleByCodeParam();
  const { user, isAuth } = useUser();

  const { doRedirectOnPrevPage } = useArticleLargeCarPagedRedirect();

  const { data: isShowedRecommendModal, doSetStorageData: setIsShowedRecommendModal } =
    useSaveData<boolean>('isShowedRecommendedArticledModal');

  const { data: recommendedData, isLoading: isArticleLoading } = useRecommendedArticleQuery({
    hash,
  });

  const { mutateAsync: makeArticleFavorite } = useMakeArticleFavoriteMutation();
  const { mutateAsync: like } = useLikeArticleMutation();
  const { mutateAsync: removeLike } = useRemoveArticleLikeMutation();

  const articleData = recommendedData?.invite?.article;
  const isAuthor = useMemo(() => articleData?.author.id === user?.id, [articleData, user]);
  const Body = useMemo(
    () => getArticleLargeCardBody({ isArticleLoading, articleData }),
    [isArticleLoading, articleData]
  );

  useLayoutEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const coach = recommendedData?.invite.recommended_by;
    const articleData = recommendedData?.invite.article;
    if (coach && articleData) {
      const fullName = getFullNameM(coach.first_name, coach.middle_name, coach.last_name);
      !isShowedRecommendModal &&
        dispatch(
          openRecommendModal({
            title: 'Рекомендация',
            specialist: `${fullName}`,
            cardName: `${articleData.title}`,
            text: 'Рекомендую вам статью:',
            photo: coach.photo,
            confirmText: 'Ок',
            confirm: () => setIsShowedRecommendModal(true),
          })
        );
    }
  }, [dispatch, isShowedRecommendModal, recommendedData, setIsShowedRecommendModal]);

  const onClosePage = useCallback(() => {
    if (!articleData?.favorite && isAuth) {
      return dispatch(
        openDialogModal({
          title: 'Уведомление',
          text: 'Для быстрого нахождения статьи, хотите добавить её в избранное?',
          closeToConfirm: true,
          onRequestClose: true,
          dinamicHeight: true,
          confirmText: 'Да',
          confirm: async () => {
            await makeArticleFavorite({
              id: articleData?.id ? String(articleData?.id) : undefined,
            });
            doRedirectOnPrevPage(`/articles/${code}`);
          },
          declineText: 'Нет',
          decline: () => doRedirectOnPrevPage(`/articles/${code}`),
        })
      );
    } else {
      doRedirectOnPrevPage('/articles');
    }
  }, [doRedirectOnPrevPage, articleData, dispatch, isAuth, makeArticleFavorite, code]);

  return (
    <>
      <PageHead
        Left={<PageBackButton onClick={onClosePage} />}
        Right={
          <>
            {articleData && isAuthor && (
              <ModerationStatusViewer
                isPublished={articleData.publication?.is_published || false}
                moderationStatus={articleData.moderation_status}
              />
            )}
            {articleData && articleData?.with_likes && (
              <LikesSmallButton
                isLiked={articleData.liked}
                likesCount={articleData.likes}
                onLike={() => like({ id: String(articleData.id) })}
                onRemoveLike={() => removeLike({ id: String(articleData.id) })}
              />
            )}
            {articleData && articleData?.with_comments && (
              <CommentsSmallButton count={articleData.comments.length} />
            )}
            {articleData && (
              <ArticleDropMenu
                isFavorite={articleData.favorite}
                id={articleData.id}
                isAuthor={isAuthor}
                authorId={articleData?.author?.coach?.id}
              />
            )}
          </>
        }
        Center={'Статья'}
      />
      <ListBody>{Body}</ListBody>
    </>
  );
});
